import AsyncStorage from '@react-native-async-storage/async-storage';
import { env } from 'app/lib/env';
import type { SuggestedListing } from 'app/network/suggested-listing';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { HubDecision } from './queue';

export interface HubRound {
  id: string;
  items: SuggestedListing[];
}
interface HubSession {
  version: number;
  tutorialCompleted: boolean;
  round: HubRound | null;
}
const emptySession = (): HubSession => ({ version: 1, tutorialCompleted: false, round: null });
export const hubSessionKey = (userId: string) =>
  `offer-hub-session:v1:${encodeURIComponent(env.API_BASE_URL)}:${userId}`;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isSuggestedListing(value: unknown): value is SuggestedListing {
  if (!isRecord(value) || typeof value.id !== 'string' || !isRecord(value.listingGroup)) return false;
  const { name, picture } = value.listingGroup;
  return (typeof name === 'string' || name === null) && (typeof picture === 'string' || picture === null);
}

function isHubRound(value: unknown): value is HubRound {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    Array.isArray(value.items) &&
    value.items.every(isSuggestedListing)
  );
}

function parseSession(raw: string | null): HubSession {
  const parsed: unknown = raw ? JSON.parse(raw) : emptySession();
  if (
    !isRecord(parsed) ||
    parsed.version !== 1 ||
    typeof parsed.tutorialCompleted !== 'boolean' ||
    (parsed.round !== null && !isRecord(parsed.round))
  )
    throw new Error('Invalid offer hub session');
  return {
    version: 1,
    tutorialCompleted: parsed.tutorialCompleted,
    // Discard old PopularItem rounds while retaining tutorial completion.
    round: isHubRound(parsed.round) ? parsed.round : null,
  };
}

export function remainingRoundItems(round: HubRound, decisions: HubDecision[]) {
  const seen = new Set(decisions.map((decision) => decision.suggestedListingId));
  return round.items.filter((item) => !seen.has(item.id));
}

/** Refresh campaign priority and eligibility without losing the round's completed decisions. */
export function refreshHubRound(round: HubRound, candidates: SuggestedListing[], decisions: HubDecision[]): HubRound {
  const seen = new Set(decisions.map((decision) => decision.suggestedListingId));
  const answered = round.items.filter((item) => seen.has(item.id));
  const available = new Map(candidates.filter((item) => !seen.has(item.id)).map((item) => [item.id, item]));
  const ordered = new Map<string, SuggestedListing>();
  for (const item of [...candidates.filter((candidate) => candidate.isPriority), ...round.items, ...candidates]) {
    const current = available.get(item.id);
    if (current && !ordered.has(item.id)) ordered.set(item.id, current);
  }
  return { ...round, items: [...answered, ...[...ordered.values()].slice(0, 10 - answered.length)] };
}

/** Only completion is durable: leaving an unfinished tutorial always restarts it. */
export function useHubSession(userId?: string) {
  const [loaded, setLoaded] = useState<string>();
  const [session, setSession] = useState<HubSession>(emptySession);
  const [storageError, setStorageError] = useState(false);
  const current = useRef(session);
  const owner = useRef<string | undefined>(undefined);
  const writes = useRef<Promise<void>>(Promise.resolve());
  // The loading effect must only restart when its owner changes.
  const read = useCallback(async () => {
    if (!userId) return;
    try {
      await writes.current;
      const raw = await AsyncStorage.getItem(hubSessionKey(userId));
      const value = parseSession(raw);
      if (owner.current !== userId) return;
      current.current = value;
      setSession(value);
      setStorageError(false);
      setLoaded(userId);
    } catch {
      if (owner.current === userId) setStorageError(true);
    }
  }, [userId]);
  useEffect(() => {
    owner.current = userId;
    void Promise.resolve().then(read);
    return () => {
      owner.current = undefined;
    };
  }, [read, userId]);
  // Consumers persist rounds in effects; saving must not retrigger those effects.
  const save = useCallback(
    (patch: Partial<HubSession>) => {
      if (!userId) return Promise.resolve(false);
      const value = { ...current.current, ...patch };
      current.current = value;
      setSession(value);
      let saved = false;
      writes.current = writes.current.then(async () => {
        try {
          await AsyncStorage.setItem(hubSessionKey(userId), JSON.stringify(value));
          saved = true;
          if (owner.current === userId) setStorageError(false);
        } catch {
          if (owner.current === userId) setStorageError(true);
        }
      });
      return writes.current.then(() => saved);
    },
    [userId],
  );
  return {
    ...session,
    ready: Boolean(userId && loaded === userId),
    storageError,
    save,
    retry: () => (loaded === userId ? save({}) : read()),
  };
}
